import PropTypes from 'prop-types';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Check } from '@mui/icons-material';

/**
 * Custom step icon component that shows checkmarks for completed steps
 */
function CustomStepIcon({ active, completed, icon, _error, ...props }) {
  const theme = useTheme();

  if (completed) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          borderRadius: '50%',
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
        {...props}
      >
        <Check sx={{ fontSize: 16 }} />
      </Box>
    );
  }

  if (active) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          borderRadius: '50%',
          width: 24,
          height: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '0.875rem',
        }}
        {...props}
      >
        {icon}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.grey[300],
        borderRadius: '50%',
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.grey[600],
        fontSize: '0.875rem',
      }}
      {...props}
    >
      {icon}
    </Box>
  );
}

CustomStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  icon: PropTypes.node,
  _error: PropTypes.bool,
};

/**
 * StepperComponent - Visual progress indicator for screen categories
 */
function StepperComponent({ steps, currentStep, onStepClick }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine orientation based on screen size
  const orientation = isMobile ? 'vertical' : 'horizontal';

  return (
    <Container maxWidth="md">
      <Paper
        elevation={2}
        sx={{
          margin: '0 auto',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '8px',
          padding: isMobile ? '16px 8px' : '16px',
        }}
      >
        {isMobile ? (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Step {currentStep + 1} of {steps.length}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}
              >
                {steps[currentStep]?.label}
              </Typography>
              {steps[currentStep]?.isOptional && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    color: theme.palette.text.secondary,
                    fontStyle: 'italic',
                  }}
                >
                  (optional)
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Stepper
            activeStep={currentStep}
            orientation={orientation}
            sx={{
              '& .MuiStepConnector-root': {
                marginLeft: 0,
                marginTop: 0,
              },
              '& .MuiStepLabel-root': {
                cursor: onStepClick ? 'pointer' : 'default',
              },
            }}
          >
            {steps.map((step, index) => (
              <Step key={step.label} completed={step.isCompleted}>
                <StepLabel
                  StepIconComponent={(props) => <CustomStepIcon {...props} icon={index + 1} />}
                  onClick={onStepClick ? () => onStepClick(index) : undefined}
                >
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: step.isActive ? 'bold' : 'normal',
                        color: step.isActive
                          ? theme.palette.primary.main
                          : step.isCompleted
                            ? theme.palette.text.primary
                            : theme.palette.text.secondary,
                      }}
                    >
                      {step.label}
                    </Typography>
                    {step.isOptional && (
                      <Typography
                        variant="smallText"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic',
                          display: 'block',
                        }}
                      >
                        (optional)
                      </Typography>
                    )}
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>
    </Container>
  );
}

StepperComponent.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      isOptional: PropTypes.bool,
      isCompleted: PropTypes.bool,
      isActive: PropTypes.bool,
    })
  ).isRequired,
  currentStep: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
};

export default StepperComponent;
